'use client'

import Link from 'next/link'
import { useMemo, useState } from 'react'
import { track } from '@/lib/analytics'
import { ordinal, type Technology } from '@/lib/portfolio'

const ORDERS = ['Op doel', 'Op naam', 'Hoe vaak'] as const

type Order = (typeof ORDERS)[number]

/** Daily first, then regularly, then occasionally — most used first. */
const FREQUENCY_RANK: Record<string, number> = {
    Dagelijks: 0,
    Regelmatig: 1,
    Soms: 2,
}

/**
 * The stack as a filterable table.
 *
 * Filtering and sorting happen here rather than through the API: the whole
 * list is forty rows, it is already on the page, and a round trip per filter
 * click would be slower than the render it replaces.
 */
export function StackTable({ technologies }: { technologies: Technology[] }) {
    const [purpose, setPurpose] = useState('Alles')
    const [order, setOrder] = useState<Order>('Op doel')

    const purposes = useMemo(() => {
        const counted = new Map<string, number>()

        for (const technology of technologies) {
            if (technology.purpose !== '') {
                counted.set(technology.purpose, (counted.get(technology.purpose) ?? 0) + 1)
            }
        }

        return [
            { name: 'Alles', count: technologies.length },
            ...Array.from(counted, ([name, count]) => ({ name, count })),
        ]
    }, [technologies])

    const rows = useMemo(() => {
        const filtered =
            purpose === 'Alles' ? technologies : technologies.filter((one) => one.purpose === purpose)

        const sorted = [...filtered]

        if (order === 'Op naam') {
            sorted.sort((a, b) => a.title.localeCompare(b.title, 'nl'))
        }

        if (order === 'Hoe vaak') {
            sorted.sort(
                (a, b) =>
                    (FREQUENCY_RANK[a.frequency] ?? 9) - (FREQUENCY_RANK[b.frequency] ?? 9) ||
                    a.title.localeCompare(b.title, 'nl'),
            )
        }

        // 'Op doel' is the order the panel put them in, which groups by
        // purpose already — so it is the list as fetched.
        return sorted
    }, [order, purpose, technologies])

    return (
        <>
            <section className="sticky top-19 z-40 border-b border-hair bg-bg max-sm:top-16">
                <div className="mx-auto max-w-band px-rail">
                    <div className="flex flex-wrap items-center justify-between gap-6 px-gutter py-[18px]">
                        <div className="flex flex-wrap gap-2">
                            {purposes.map((item) => (
                                <button
                                    key={item.name}
                                    type="button"
                                    onClick={() => {
                                        setPurpose(item.name)
                                        track({
                                            event: 'stack_filter',
                                            filter_type: 'purpose',
                                            filter_value: item.name,
                                        })
                                    }}
                                    aria-pressed={purpose === item.name}
                                    className={`cursor-pointer rounded-full border px-4 py-[9px] font-mono text-[11.5px] tracking-[0.04em] transition duration-200 ${
                                        purpose === item.name
                                            ? 'border-accent text-accent'
                                            : 'border-hair text-dim hover:border-accent hover:text-accent'
                                    }`}
                                >
                                    {item.name} <span className="opacity-50">{item.count}</span>
                                </button>
                            ))}
                        </div>
                        <div className="flex items-center gap-2.5">
                            <span className="font-mono text-[10.5px] tracking-[0.08em] text-faint">
                                SORTEER
                            </span>
                            {ORDERS.map((option) => (
                                <button
                                    key={option}
                                    type="button"
                                    onClick={() => {
                                        setOrder(option)
                                        track({
                                            event: 'stack_filter',
                                            filter_type: 'order',
                                            filter_value: option,
                                        })
                                    }}
                                    aria-pressed={order === option}
                                    className={`cursor-pointer border-0 border-b bg-transparent px-0.5 py-1.5 font-mono text-[11.5px] transition duration-200 ${
                                        order === option
                                            ? 'border-b-accent text-accent'
                                            : 'border-b-transparent text-dim hover:text-fg'
                                    }`}
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="border-b border-hair">
                <div className="mx-auto max-w-band px-rail">
                    <div className="grid grid-cols-[44px_4fr_3fr_5fr] gap-6 border-b border-hair px-gutter py-3.5 font-mono text-[10.5px] tracking-[0.08em] text-faint max-lg:hidden">
                        <span>#</span>
                        <span>NAAM</span>
                        <span>DOEL</span>
                        <span>WAAR IK HET VOOR GEBRUIK</span>
                    </div>
                    {rows.map((technology, index) => (
                        <Link
                            key={technology.slug}
                            href={technology.url}
                            className="grid grid-cols-[44px_4fr_3fr_5fr] items-center gap-6 border-b border-hair px-gutter py-7 transition-[padding-left,background] duration-500 ease-design hover:bg-hover hover:pl-[46px] max-lg:grid-cols-1 max-lg:gap-2"
                        >
                            <span className="font-mono text-[11px] text-faint">{ordinal(index)}</span>
                            <span className="text-xl tracking-[-0.025em]">{technology.title}</span>
                            <span className="font-mono text-[11px] tracking-[0.04em] text-accent">
                                {technology.purpose}
                            </span>
                            <span className="text-[15px] leading-[1.55] text-pretty text-dim">
                                {technology.summary}
                            </span>
                        </Link>
                    ))}
                </div>
            </section>
        </>
    )
}
